"use client"

import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  createInitialState,
  getLegalMoves,
  executeMove,
  isCheck,
  getBestMove,
  PIECE_VALUES
} from './chess/engine.js'
import { ChessPiece } from './chess/ChessPieces.jsx'
import { chessAudio } from './chess/audio.js'

const PIECE_NAMES = {
  p: 'Pawn', n: 'Knight', b: 'Bishop', r: 'Rook', q: 'Queen', k: 'King'
}

export default function ChessApp() {
  const [gameState, setGameState] = useState(createInitialState)
  const [historyStack, setHistoryStack] = useState([])
  const [selectedSquare, setSelectedSquare] = useState(null) // { r, c }
  const [gameMode, setGameMode] = useState('ai') // 'ai' | 'pvp'
  const [difficulty, setDifficulty] = useState('medium') // 'easy' | 'medium' | 'hard'
  const [isAiThinking, setIsAiThinking] = useState(false)
  const [isFlipped, setIsFlipped] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState(true)
  const [pendingPromotion, setPendingPromotion] = useState(null) // { move }
  const [activeTab, setActiveTab] = useState('board') // 'board' | 'history'

  useEffect(() => {
    return () => {
      chessAudio.destroy()
    }
  }, [])

  const currentLegalMoves = useMemo(() => {
    return getLegalMoves(gameState, gameState.turn)
  }, [gameState])

  const checkStatus = useMemo(() => {
    return isCheck(gameState.board, gameState.turn)
  }, [gameState.board, gameState.turn])

  // Material evaluation difference
  const materialAdvantage = useMemo(() => {
    let wScore = 0
    let bScore = 0
    for (const p of gameState.captured.w) wScore += PIECE_VALUES[p.toUpperCase()] || 0
    for (const p of gameState.captured.b) bScore += PIECE_VALUES[p.toUpperCase()] || 0
    return {
      white: Math.max(0, Math.round((wScore - bScore) / 100)),
      black: Math.max(0, Math.round((bScore - wScore) / 100))
    }
  }, [gameState.captured])

  const playMoveSound = useCallback((targetMove, nextState) => {
    if (!soundEnabled) return
    if (nextState.status === 'white_win' || nextState.status === 'black_win' || nextState.status === 'stalemate') {
      chessAudio.playGameOver()
    } else if (isCheck(nextState.board, nextState.turn)) {
      chessAudio.playCheck()
    } else if (targetMove.isEnPassant || !!gameState.board[targetMove.to.r][targetMove.to.c]) {
      chessAudio.playCapture()
    } else {
      chessAudio.playMove()
    }
  }, [soundEnabled, gameState.board])

  // AI Move triggering
  useEffect(() => {
    const isGameOver = gameState.status === 'white_win' || gameState.status === 'black_win' || gameState.status === 'stalemate'
    if (gameMode === 'ai' && gameState.turn === 'b' && !isGameOver && !pendingPromotion) {
      setIsAiThinking(true)
      const timer = setTimeout(() => {
        const bestMove = getBestMove(gameState, difficulty)
        if (bestMove) {
          setHistoryStack(prev => [...prev, gameState])
          const nextState = executeMove(gameState, bestMove)
          setGameState(nextState)
          playMoveSound(bestMove, nextState)
        }
        setIsAiThinking(false)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [gameState, gameMode, difficulty, pendingPromotion, playMoveSound])

  const handleSquareClick = (r, c) => {
    const isGameOver = gameState.status === 'white_win' || gameState.status === 'black_win' || gameState.status === 'stalemate'
    if (isGameOver || (gameMode === 'ai' && gameState.turn === 'b')) return
    if (pendingPromotion) return

    const clickedPiece = gameState.board[r][c]
    const isCurrentPlayerPiece = clickedPiece && (
      (gameState.turn === 'w' && clickedPiece === clickedPiece.toUpperCase()) ||
      (gameState.turn === 'b' && clickedPiece === clickedPiece.toLowerCase())
    )

    if (selectedSquare) {
      // Check if clicking another friendly piece
      if (isCurrentPlayerPiece) {
        setSelectedSquare({ r, c })
        return
      }

      // Check if this is a legal move
      const targetMove = currentLegalMoves.find(
        m => m.from.r === selectedSquare.r && m.from.c === selectedSquare.c && m.to.r === r && m.to.c === c
      )

      if (targetMove) {
        // Handle pawn promotion if applicable
        const piece = gameState.board[selectedSquare.r][selectedSquare.c]
        const isPawn = piece && piece.toLowerCase() === 'p'
        const promoRank = gameState.turn === 'w' ? 0 : 7
        if (isPawn && r === promoRank) {
          setPendingPromotion({ from: selectedSquare, to: { r, c } })
          return
        }

        // Standard move execution
        setHistoryStack(prev => [...prev, gameState])
        const nextState = executeMove(gameState, targetMove)
        setGameState(nextState)
        setSelectedSquare(null)
        playMoveSound(targetMove, nextState)
      } else {
        setSelectedSquare(null)
      }
    } else {
      if (isCurrentPlayerPiece) {
        setSelectedSquare({ r, c })
      }
    }
  }

  const handleConfirmPromotion = (promoType) => {
    if (!pendingPromotion) return
    const promoPiece = gameState.turn === 'w' ? promoType.toUpperCase() : promoType.toLowerCase()
    const targetMove = currentLegalMoves.find(
      m => m.from.r === pendingPromotion.from.r &&
           m.from.c === pendingPromotion.from.c &&
           m.to.r === pendingPromotion.to.r &&
           m.to.c === pendingPromotion.to.c &&
           m.promo && m.promo.toUpperCase() === promoType.toUpperCase()
    ) || { ...pendingPromotion, promo: promoPiece }

    setHistoryStack(prev => [...prev, gameState])
    const nextState = executeMove(gameState, targetMove)
    setGameState(nextState)
    setPendingPromotion(null)
    setSelectedSquare(null)
    playMoveSound(targetMove, nextState)
  }

  const handleUndo = () => {
    if (historyStack.length === 0 || isAiThinking) return
    // In AI mode, undo both AI and player turn if possible
    if (gameMode === 'ai' && historyStack.length >= 2) {
      const targetState = historyStack[historyStack.length - 2]
      setHistoryStack(prev => prev.slice(0, prev.length - 2))
      setGameState(targetState)
    } else {
      const targetState = historyStack[historyStack.length - 1]
      setHistoryStack(prev => prev.slice(0, prev.length - 1))
      setGameState(targetState)
    }
    setSelectedSquare(null)
    setPendingPromotion(null)
  }

  const handleResetGame = () => {
    setGameState(createInitialState())
    setHistoryStack([])
    setSelectedSquare(null)
    setPendingPromotion(null)
  }

  // Highlight calculations
  const destinationSquares = useMemo(() => {
    if (!selectedSquare) return new Map()
    const map = new Map()
    currentLegalMoves
      .filter(m => m.from.r === selectedSquare.r && m.from.c === selectedSquare.c)
      .forEach(m => {
        const key = `${m.to.r},${m.to.c}`
        const isCapture = !!gameState.board[m.to.r][m.to.c] || m.isEnPassant
        map.set(key, isCapture)
      })
    return map
  }, [selectedSquare, currentLegalMoves, gameState.board])

  const lastMove = gameState.history[gameState.history.length - 1]

  const renderedRows = isFlipped ? [7, 6, 5, 4, 3, 2, 1, 0] : [0, 1, 2, 3, 4, 5, 6, 7]
  const renderedCols = isFlipped ? [7, 6, 5, 4, 3, 2, 1, 0] : [0, 1, 2, 3, 4, 5, 6, 7]

  return (
    <div className="flex flex-col h-full bg-[#12141a] text-white select-none overflow-hidden font-sans">
      {/* Top Bar / Controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 bg-black/40 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-amber-500/20 text-amber-400">
            <i className="fas fa-chess text-lg"></i>
          </div>
          <div>
            <h2 className="text-sm font-bold tracking-wide text-white flex items-center gap-2">
              Pink Pixel Chess
              {checkStatus && gameState.status === 'playing' && (
                <span className="text-[10px] px-2 py-0.5 rounded bg-rose-500/30 text-rose-300 font-semibold border border-rose-500/40">
                  CHECK
                </span>
              )}
            </h2>
            <p className="text-[11px] text-gray-400">
              {gameState.status === 'white_win' && 'Checkmate • White is Victorious!'}
              {gameState.status === 'black_win' && 'Checkmate • Black is Victorious!'}
              {gameState.status === 'stalemate' && 'Draw • Stalemate'}
              {gameState.status === 'playing' && (
                <>
                  {lastMove && (
                    <span className="mr-1.5">
                      <span className="text-gray-300 font-medium">Last: {lastMove.san}</span>
                      {lastMove.captured && (
                        <span className="text-rose-400 font-semibold ml-1">
                          (Captured {PIECE_NAMES[lastMove.captured.toLowerCase()] || 'Piece'})
                        </span>
                      )}
                      {' • '}
                    </span>
                  )}
                  {gameState.turn === 'w' ? 'White to move' : (isAiThinking ? 'AI thinking...' : 'Black to move')}
                </>
              )}
            </p>
          </div>
        </div>

        {/* Toolbar buttons */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => {
              setSoundEnabled(s => {
                chessAudio.muted = s
                return !s
              })
            }}
            className="p-1.5 text-xs rounded-md bg-white/10 hover:bg-white/20 text-gray-200 transition-colors border border-white/10"
            title={soundEnabled ? 'Mute Sound' : 'Unmute Sound'}
            aria-label={soundEnabled ? 'Mute Sound' : 'Unmute Sound'}
          >
            <i className={`fas ${soundEnabled ? 'fa-volume-high text-emerald-400' : 'fa-volume-xmark text-gray-400'}`}></i>
          </button>

          <button
            onClick={() => setGameMode(m => m === 'ai' ? 'pvp' : 'ai')}
            className="px-2.5 py-1 text-xs rounded-md bg-white/10 hover:bg-white/20 text-gray-200 transition-colors border border-white/10 flex items-center gap-1.5"
            title="Toggle AI or 2-Player mode"
          >
            <i className={`fas ${gameMode === 'ai' ? 'fa-robot text-purple-400' : 'fa-user-friends text-blue-400'}`}></i>
            <span className="hidden sm:inline">{gameMode === 'ai' ? 'vs AI' : 'Pass & Play'}</span>
          </button>

          {gameMode === 'ai' && (
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="px-2 py-1 text-xs rounded-md bg-white/10 text-gray-200 border border-white/10 focus:outline-none focus:ring-1 focus:ring-amber-400"
              aria-label="AI Difficulty"
            >
              <option value="easy" className="bg-gray-900 text-white">Easy</option>
              <option value="medium" className="bg-gray-900 text-white">Medium</option>
              <option value="hard" className="bg-gray-900 text-white">Hard</option>
            </select>
          )}

          <button
            onClick={() => setIsFlipped(f => !f)}
            className="p-1.5 text-xs rounded-md bg-white/10 hover:bg-white/20 text-gray-200 transition-colors border border-white/10"
            title="Flip Board"
            aria-label="Flip Board"
          >
            <i className="fas fa-arrows-rotate"></i>
          </button>

          <button
            onClick={handleUndo}
            disabled={historyStack.length === 0 || isAiThinking}
            className="p-1.5 text-xs rounded-md bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:hover:bg-white/10 text-gray-200 transition-colors border border-white/10"
            title="Undo Move"
            aria-label="Undo Move"
          >
            <i className="fas fa-rotate-left"></i>
          </button>

          <button
            onClick={handleResetGame}
            className="px-2.5 py-1 text-xs rounded-md bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 transition-colors border border-amber-500/30 font-medium"
          >
            New Game
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col md:flex-row items-center justify-center p-3 sm:p-4 overflow-y-auto overflow-x-hidden gap-4">
        {/* Board Container */}
        <div className="flex flex-col items-center max-w-full shrink-0">
          {/* Top Player Info / Captures */}
          <div className="w-full max-w-[460px] flex items-center justify-between px-1 py-1 text-xs text-gray-300">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-200">{isFlipped ? 'White' : (gameMode === 'ai' ? `AI (${difficulty})` : 'Black')}</span>
              {materialAdvantage.black > 0 && !isFlipped && (
                <span className="text-[10px] px-1 rounded bg-white/10 text-white font-bold">+{materialAdvantage.black}</span>
              )}
              {materialAdvantage.white > 0 && isFlipped && (
                <span className="text-[10px] px-1 rounded bg-white/10 text-white font-bold">+{materialAdvantage.white}</span>
              )}
            </div>
            <div className="flex items-center gap-0.5 min-h-[22px] max-w-[200px] overflow-hidden">
              {(isFlipped ? gameState.captured.w : gameState.captured.b).map((p, i) => (
                <span key={i} className="w-4 h-4 inline-block opacity-80">
                  <ChessPiece piece={p} />
                </span>
              ))}
            </div>
          </div>

          {/* 8x8 Chessboard */}
          <div className="relative p-2 bg-[#1b1e24] rounded-xl shadow-2xl border border-white/10">
            <div
              className="grid grid-cols-8 grid-rows-8 w-[82vw] h-[82vw] max-w-[440px] max-h-[440px] sm:w-[440px] sm:h-[440px] border border-black/40 rounded-lg overflow-hidden"
              style={{ touchAction: 'manipulation' }}
            >
              {renderedRows.map(r => (
                renderedCols.map(c => {
                  const piece = gameState.board[r][c]
                  const isDark = (r + c) % 2 === 1
                  const isSelected = selectedSquare && selectedSquare.r === r && selectedSquare.c === c
                  const key = `${r},${c}`
                  const isLegalTarget = destinationSquares.has(key)
                  const isCapture = destinationSquares.get(key)
                  const isLastMoveTarget = lastMove && lastMove.to.r === r && lastMove.to.c === c
                  const isLastMoveSource = lastMove && lastMove.from.r === r && lastMove.from.c === c
                  const isCaptureSquare = isLastMoveTarget && !!lastMove.captured
                  const isLastMoveSquare = isLastMoveTarget || isLastMoveSource
                  const isKingInCheck = checkStatus && piece && piece.toLowerCase() === 'k' && (
                    (gameState.turn === 'w' && piece === 'K') || (gameState.turn === 'b' && piece === 'k')
                  )

                  return (
                    <button
                      key={key}
                      onClick={() => handleSquareClick(r, c)}
                      className={`relative flex items-center justify-center p-1 transition-colors cursor-pointer focus:outline-none ${
                        isDark ? 'bg-[#334155]' : 'bg-[#94a3b8]'
                      } ${isCaptureSquare ? 'ring-inset ring-2 ring-rose-500/80 bg-rose-500/20' : isLastMoveSquare ? 'ring-inset ring-2 ring-amber-400/50 bg-amber-400/10' : ''} ${
                        isSelected ? 'bg-blue-600/60 ring-inset ring-2 ring-blue-300' : ''
                      } ${isKingInCheck ? 'bg-red-600/70 ring-inset ring-2 ring-red-400 animate-pulse' : ''}`}
                      aria-label={`Square ${String.fromCharCode(97 + c)}${8 - r}${piece ? ` with ${piece}` : ''}`}
                    >
                      {/* Chess Piece Graphic */}
                      {piece && (
                        <div className="w-full h-full p-0.5 pointer-events-none drop-shadow-md transition-transform hover:scale-105">
                          <ChessPiece piece={piece} />
                        </div>
                      )}

                      {/* Legal Move Indicator */}
                      {isLegalTarget && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          {isCapture ? (
                            <div className="w-[85%] h-[85%] rounded-full border-4 border-rose-500/80"></div>
                          ) : (
                            <div className="w-3.5 h-3.5 rounded-full bg-blue-400/90 shadow-sm"></div>
                          )}
                        </div>
                      )}

                      {/* Rank / File Coordinate Labels */}
                      {c === (isFlipped ? 7 : 0) && (
                        <span className={`absolute top-0.5 left-0.5 text-[9px] font-bold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                          {8 - r}
                        </span>
                      )}
                      {r === (isFlipped ? 0 : 7) && (
                        <span className={`absolute bottom-0.5 right-0.5 text-[9px] font-bold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                          {String.fromCharCode(97 + c)}
                        </span>
                      )}
                    </button>
                  )
                })
              ))}
            </div>

            {/* Game Over Overlay */}
            {(gameState.status === 'white_win' || gameState.status === 'black_win' || gameState.status === 'stalemate') && (
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center rounded-xl z-20">
                <div className="p-3 bg-white/10 rounded-2xl mb-3 text-amber-400">
                  <i className="fas fa-trophy text-4xl"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-1">
                  {gameState.status === 'white_win' && 'White Wins by Checkmate!'}
                  {gameState.status === 'black_win' && 'Black Wins by Checkmate!'}
                  {gameState.status === 'stalemate' && 'Draw by Stalemate'}
                </h3>
                <p className="text-xs text-gray-300 mb-4">
                  Match completed in {gameState.fullMoves} moves.
                </p>
                <button
                  onClick={handleResetGame}
                  className="px-5 py-2 text-sm font-semibold rounded-lg bg-amber-500 hover:bg-amber-400 text-black transition-colors shadow-lg"
                >
                  Play Again
                </button>
              </div>
            )}

            {/* Promotion Modal Overlay */}
            {pendingPromotion && (
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center rounded-xl z-30">
                <h4 className="text-base font-bold text-white mb-4">Promote Pawn</h4>
                <div className="flex gap-3 p-3 bg-white/10 rounded-xl border border-white/20">
                  {['Q', 'R', 'B', 'N'].map((type) => {
                    const pieceChar = gameState.turn === 'w' ? type : type.toLowerCase()
                    return (
                      <button
                        key={type}
                        onClick={() => handleConfirmPromotion(type)}
                        className="w-14 h-14 p-2 bg-black/40 hover:bg-white/20 rounded-lg border border-white/20 transition-all flex items-center justify-center"
                        aria-label={`Promote to ${type}`}
                      >
                        <ChessPiece piece={pieceChar} />
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Bottom Player Info / Captures */}
          <div className="w-full max-w-[460px] flex items-center justify-between px-1 py-1 text-xs text-gray-300">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-200">{isFlipped ? (gameMode === 'ai' ? `AI (${difficulty})` : 'Black') : 'White'}</span>
              {materialAdvantage.white > 0 && !isFlipped && (
                <span className="text-[10px] px-1 rounded bg-white/10 text-white font-bold">+{materialAdvantage.white}</span>
              )}
              {materialAdvantage.black > 0 && isFlipped && (
                <span className="text-[10px] px-1 rounded bg-white/10 text-white font-bold">+{materialAdvantage.black}</span>
              )}
            </div>
            <div className="flex items-center gap-0.5 min-h-[22px] max-w-[200px] overflow-hidden">
              {(isFlipped ? gameState.captured.b : gameState.captured.w).map((p, i) => (
                <span key={i} className="w-4 h-4 inline-block opacity-80">
                  <ChessPiece piece={p} />
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Move History Panel */}
        <div className="w-full md:w-56 h-40 md:h-[490px] bg-black/30 rounded-xl border border-white/10 flex flex-col p-3 text-xs shrink-0">
          <div className="flex items-center justify-between border-b border-white/10 pb-2 mb-2">
            <span className="font-bold text-gray-200 flex items-center gap-1.5">
              <i className="fas fa-list-ol text-gray-400"></i>
              Move History
            </span>
            <span className="text-[10px] text-gray-400">{Math.ceil(gameState.history.length / 2)} turns</span>
          </div>

          <div className="flex-1 overflow-y-auto space-y-1 pr-1">
            {gameState.history.length === 0 ? (
              <div className="text-gray-500 italic py-4 text-center">No moves yet</div>
            ) : (
              Array.from({ length: Math.ceil(gameState.history.length / 2) }).map((_, idx) => {
                const whiteMove = gameState.history[idx * 2]
                const blackMove = gameState.history[idx * 2 + 1]
                return (
                  <div key={idx} className="flex items-center text-gray-300 py-0.5 px-1 rounded hover:bg-white/5 font-mono text-[11px]">
                    <span className="w-7 text-gray-500 text-right mr-2">{idx + 1}.</span>
                    <span className="w-16 font-semibold text-white">{whiteMove?.san || ''}</span>
                    <span className="w-16 text-gray-300">{blackMove?.san || ''}</span>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
